import React, { useState } from 'react';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { useRTL } from '../shared/hooks/useRTL';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

const MerchantRegistration = () => {
  const { t } = useLanguage(); // eslint-disable-line no-unused-vars
  const { isRTL } = useRTL(); // eslint-disable-line no-unused-vars
  
  // Helper function to validate URL
  // eslint-disable-next-line no-unused-vars
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Helper function to upload files
  const uploadFile = async (file, type = 'merchant') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const response = await fetch('https://nodjswebhook.onrender.com/api/upload/merchant', {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        return result.data.url;
      } else {
        throw new Error(result.error || 'فشل في رفع الملف');
      }
    } catch (error) {
      console.error('خطأ في رفع الملف:', error);
      throw error;
    }
  };
  
  // Form state
  const [formData, setFormData] = useState({
    business_name: '',
    legal_name: '',
    cr_number: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    business_category: '',
    bank_account_iban: '',
    bank_account_holder: ''
  });

  const [offers, setOffers] = useState([{
    id: 1,
    offer_title: '',
    offer_description: '',
    discount_percentage: '',
    valid_from: '',
    valid_until: '',
    max_uses: '',
    min_wallet_balance: '',
    products: []
  }]);

  const [galleryImages, setGalleryImages] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const businessCategories = [
    { value: 'restaurant', label: 'مطعم' },
    { value: 'coffee', label: 'قهوة' },
    { value: 'retail', label: 'بيع بالتجزئة' },
    { value: 'services', label: 'خدمات' },
    { value: 'electronics', label: 'إلكترونيات' },
    { value: 'fashion', label: 'أزياء' },
    { value: 'health', label: 'صحة وطب' },
    { value: 'education', label: 'تعليم' },
    { value: 'other', label: 'أخرى' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCoverImageChange = (e) => {
    setCoverImage(e.target.files[0]);
  };

  const handleGalleryImageChange = (index, file) => {
    const newGalleryImages = [...galleryImages];
    newGalleryImages[index] = file;
    setGalleryImages(newGalleryImages);
  };

  const addGalleryImage = () => {
    setGalleryImages([...galleryImages, null]);
  };

  const removeGalleryImage = (index) => {
    const newGalleryImages = galleryImages.filter((_, i) => i !== index);
    setGalleryImages(newGalleryImages);
  };

  const addOffer = () => {
    const newOffer = {
      id: Date.now(),
      offer_title: '',
      offer_description: '',
      discount_percentage: '',
      valid_from: '',
      valid_until: '',
      max_uses: '',
      min_wallet_balance: '',
      products: []
    };
    setOffers([...offers, newOffer]);
  };

  const removeOffer = (offerId) => {
    setOffers(offers.filter(offer => offer.id !== offerId));
  };

  const updateOffer = (offerId, field, value) => {
    setOffers(offers.map(offer => 
      offer.id === offerId ? { ...offer, [field]: value } : offer
    ));
  };

  const addProduct = (offerId) => {
    setOffers(offers.map(offer => 
      offer.id === offerId 
        ? { ...offer, products: [...offer.products, { name: '', description: '', price: '', category: '', stock: -1, image_url: '' }] }
        : offer
    ));
  };

  const removeProduct = (offerId, productIndex) => {
    setOffers(offers.map(offer => 
      offer.id === offerId 
        ? { ...offer, products: offer.products.filter((_, index) => index !== productIndex) }
        : offer
    ));
  };

  const updateProduct = (offerId, productIndex, field, value) => {
    setOffers(offers.map(offer => 
      offer.id === offerId 
        ? { 
            ...offer, 
            products: offer.products.map((product, index) => 
              index === productIndex ? { ...product, [field]: value } : product
            )
          }
        : offer
    ));
  };

  const handleProductImageChange = (offerId, productIndex, file) => {
    setOffers(offers.map(offer => 
      offer.id === offerId 
        ? { 
            ...offer, 
            products: offer.products.map((product, index) => 
              index === productIndex ? { ...product, imageFile: file } : product
            )
          }
        : offer
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    // Validate required fields
    const requiredFields = ['business_name', 'legal_name', 'cr_number', 'contact_person', 'phone', 'email', 'address', 'city', 'business_category'];
    const missingFields = requiredFields.filter(field => !formData[field] || formData[field].trim() === '');
    
    if (missingFields.length > 0) {
      setError(`الحقول التالية مطلوبة: ${missingFields.join(', ')}`);
      setLoading(false);
      return;
    }

    if (!coverImage) {
      setError('صورة الغلاف مطلوبة');
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('تنسيق البريد الإلكتروني غير صحيح');
      setLoading(false);
      return;
    }

    // Validate phone format (basic validation)
    const phoneRegex = /^[+]?[0-9\s\-()]{10,}$/;
    if (!phoneRegex.test(formData.phone)) {
      setError('تنسيق رقم الهاتف غير صحيح');
      setLoading(false);
      return;
    }

    try {
      // Use FormData to send files with data
      const formDataToSend = new FormData();
      
      // Add basic merchant data
      formDataToSend.append('business_name', formData.business_name.trim());
      formDataToSend.append('legal_name', formData.legal_name.trim());
      formDataToSend.append('cr_number', formData.cr_number.trim());
      formDataToSend.append('contact_person', formData.contact_person.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('address', formData.address.trim());
      formDataToSend.append('city', formData.city.trim());
      formDataToSend.append('business_category', formData.business_category.trim());

      // Add optional fields if they have values
      if (formData.bank_account_iban && formData.bank_account_iban.trim() !== '') {
        formDataToSend.append('bank_account_iban', formData.bank_account_iban.trim());
      }
      if (formData.bank_account_holder && formData.bank_account_holder.trim() !== '') {
        formDataToSend.append('bank_account_holder', formData.bank_account_holder.trim());
      }

      // Add cover image
      if (coverImage) {
        formDataToSend.append('cover_image', coverImage);
      }

      // Add gallery images
      galleryImages.forEach((image, index) => {
        if (image) {
          formDataToSend.append(`gallery_image_${index + 1}`, image);
        }
      });

      // Step 1: Upload all product images first (Batch Upload)
      console.log('🚀 Step 1: Batch uploading product images...');
      const productImageUrls = new Map(); // Store product image URLs
      
      const validOffers = offers.filter(offer => offer.offer_title && offer.offer_title.trim() !== '');
      console.log('🔍 Processing offers:', validOffers.length);
      
      // Upload all product images in parallel
      const uploadPromises = [];
      for (let offerIndex = 0; offerIndex < validOffers.length; offerIndex++) {
        const offer = validOffers[offerIndex];
        const originalOffer = offers.find(o => o.offer_title === offer.offer_title);
        const validProducts = offer.products.filter(product => product.name && product.name.trim() !== '');
        
        for (let productIndex = 0; productIndex < validProducts.length; productIndex++) {
          const product = validProducts[productIndex];
          const originalProduct = originalOffer?.products[productIndex];
          
          if (originalProduct?.imageFile) {
            const uploadPromise = uploadFile(originalProduct.imageFile, 'merchant-product')
              .then(url => {
                const key = `${offerIndex}-${productIndex}`;
                productImageUrls.set(key, url);
                console.log(`✅ Product image uploaded: ${product.name} -> ${url}`);
                return { key, url, productName: product.name };
              })
              .catch(error => {
                console.error(`❌ Error uploading image for ${product.name}:`, error);
                return { key: `${offerIndex}-${productIndex}`, url: '', productName: product.name, error };
              });
            
            uploadPromises.push(uploadPromise);
          }
        }
      }
      
      // Wait for all uploads to complete
      console.log(`📤 Uploading ${uploadPromises.length} product images in parallel...`);
      const uploadResults = await Promise.all(uploadPromises);
      console.log(`✅ Batch upload completed: ${uploadResults.length} images processed`);
      
      // Step 2: Build offers with uploaded image URLs
      console.log('🚀 Step 2: Building offers with image URLs...');
      const updatedOffers = [];
      
      for (let offerIndex = 0; offerIndex < validOffers.length; offerIndex++) {
        const offer = validOffers[offerIndex];
        
        const updatedOffer = {
          offer_title: offer.offer_title.trim(),
          offer_description: offer.offer_description?.trim() || '',
          discount_percentage: parseFloat(offer.discount_percentage) || 0,
          valid_from: offer.valid_from || null,
          valid_until: offer.valid_until || null,
          max_uses: offer.max_uses ? parseInt(offer.max_uses) : null,
          min_wallet_balance: offer.min_wallet_balance ? parseFloat(offer.min_wallet_balance) : null,
          products: []
        };
        
        // Process products with uploaded image URLs
        const validProducts = offer.products.filter(product => product.name && product.name.trim() !== '');
        console.log(`🔍 Processing ${validProducts.length} products for offer: ${offer.offer_title}`);
        
        for (let productIndex = 0; productIndex < validProducts.length; productIndex++) {
          const product = validProducts[productIndex];
          const key = `${offerIndex}-${productIndex}`;
          const imageUrl = productImageUrls.get(key) || '';
          
          const updatedProduct = {
            product_name: product.name.trim(),
            description: product.description?.trim() || '',
            original_price: parseFloat(product.price) || 0,
            category: product.category?.trim() || '',
            stock_quantity: parseInt(product.stock) || -1,
            image_url: imageUrl // ✅ Use uploaded image URL
          };
          
          updatedOffer.products.push(updatedProduct);
          console.log(`📦 Product: ${product.name} -> Image: ${imageUrl ? '✅' : '❌'}`);
        }
        
        updatedOffers.push(updatedOffer);
        console.log(`✅ Offer processed: ${offer.offer_title} with ${updatedOffer.products.length} products`);
      }

      // Add offers as JSON string (as expected by the backend)
      const offersJsonString = JSON.stringify(updatedOffers);
      formDataToSend.append('offers', offersJsonString);
      console.log('📤 Offers data:', JSON.stringify(updatedOffers, null, 2));
      console.log('📤 Offers JSON string length:', offersJsonString.length);
      console.log('📤 Offers JSON string preview:', offersJsonString.substring(0, 200) + '...');

      // Add gallery metadata
      const galleryMetadata = galleryImages.map((image, index) => ({
        alt: `Gallery image ${index + 1}`,
        is_primary: index === 0
      }));
      formDataToSend.append('gallery_metadata', JSON.stringify(galleryMetadata));


      console.log('📤 Sending FormData with files...');
      console.log('📊 Form data summary:');
      console.log('- Basic fields:', Object.keys(formData).length);
      console.log('- Cover image:', coverImage ? 'Yes' : 'No');
      console.log('- Gallery images:', galleryImages.filter(img => img).length);
      console.log('- Offers:', updatedOffers.length);
      console.log('- Total products:', updatedOffers.reduce((sum, offer) => sum + offer.products.length, 0));
      
      // Debug: Log all FormData entries
      console.log('🔍 FormData contents:');
      for (let [key, value] of formDataToSend.entries()) {
        if (key === 'offers') {
          console.log(`📋 ${key}:`, JSON.parse(value));
        } else if (key === 'gallery_metadata') {
          console.log(`📋 ${key}:`, JSON.parse(value));
        } else {
          console.log(`📋 ${key}:`, value);
        }
      }

      const response = await fetch('https://nodjswebhook.onrender.com/api/public/merchant-registration', {
        method: 'POST',
        body: formDataToSend
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', response.headers);

      let result;
      try {
        result = await response.json();
        console.log('📡 Response data:', result);
      } catch (parseError) {
        console.error('❌ Error parsing response:', parseError);
        setError('خطأ في تحليل استجابة الخادم');
        return;
      }
      
      if (response.ok && result.success) {
        setResponse(result);
        // Reset form
        setFormData({
          business_name: '',
          legal_name: '',
          cr_number: '',
          contact_person: '',
          phone: '',
          email: '',
          address: '',
          city: '',
          business_category: '',
          bank_account_iban: '',
          bank_account_holder: ''
        });
        setOffers([{
          id: 1,
          offer_title: '',
          offer_description: '',
          discount_percentage: '',
          valid_from: '',
          valid_until: '',
          max_uses: '',
          min_wallet_balance: '',
          products: []
        }]);
        setGalleryImages([]);
        setCoverImage(null);
      } else {
        console.error('❌ API Error:', result);
        
        // Handle different types of errors
        let errorMessage = result.error || result.message || `خطأ في الخادم (${response.status})`;
        
        if (result.details && Array.isArray(result.details)) {
          const validationErrors = result.details.map(detail => 
            `${detail.path}: ${detail.msg}`
          ).join('\n');
          errorMessage = `أخطاء في التحقق:\n${validationErrors}`;
        }
        
        if (result.code === 'INTERNAL_SERVER_ERROR') {
          errorMessage = 'خطأ داخلي في الخادم. يرجى المحاولة مرة أخرى أو الاتصال بالدعم الفني.';
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('خطأ في الاتصال: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <PageHeader
          className="mb-8 flex-col items-center text-center sm:items-center"
          title="🏪 تسجيل التاجر"
          description="انضم إلى شبكة التجار وابدأ رحلتك مع  تاق 🙏"
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>📋 معلومات التاجر</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="business_name">
                    اسم المتجر <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="business_name"
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="legal_name">
                    الاسم القانوني <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="legal_name"
                    type="text"
                    name="legal_name"
                    value={formData.legal_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cr_number">
                    رقم السجل التجاري <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="cr_number"
                    type="text"
                    name="cr_number"
                    value={formData.cr_number}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_person">
                    اسم الشخص المسؤول <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contact_person"
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    رقم الهاتف <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    البريد الإلكتروني <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">
                    العنوان <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="address"
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">
                    المدينة <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="city"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>
                    فئة النشاط التجاري <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.business_category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, business_category: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الفئة" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessCategories.map(category => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_account_iban">رقم الحساب البنكي (اختياري)</Label>
                  <Input
                    id="bank_account_iban"
                    type="text"
                    name="bank_account_iban"
                    value={formData.bank_account_iban}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_account_holder">اسم صاحب الحساب (اختياري)</Label>
                  <Input
                    id="bank_account_holder"
                    type="text"
                    name="bank_account_holder"
                    value={formData.bank_account_holder}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cover Image */}
          <Card>
            <CardHeader>
              <CardTitle>🖼️ صورة الغلاف</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="cover_image">
                اختر صورة الغلاف <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cover_image"
                type="file"
                accept="image/*"
                onChange={handleCoverImageChange}
                required
              />
              {coverImage && (
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(coverImage)}
                    alt="Cover preview"
                    className="h-32 w-32 rounded-md object-cover"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Gallery Images */}
          <Card>
            <CardHeader>
              <CardTitle>📸 معرض الصور</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {galleryImages.map((image, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-medium text-foreground">صورة {index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeGalleryImage(index)}
                      >
                        حذف
                      </Button>
                    </div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleGalleryImageChange(index, e.target.files[0])}
                    />
                    {image && (
                      <div className="mt-2">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Gallery ${index + 1}`}
                          className="h-24 w-24 rounded-md object-cover"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              <Button type="button" variant="outline" onClick={addGalleryImage}>
                + إضافة صورة
              </Button>
            </CardContent>
          </Card>

          {/* Offers */}
          <Card>
            <CardHeader>
              <CardTitle>🎯 العروض والخصومات</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {offers.map((offer, offerIndex) => (
                <Card key={offer.id}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="text-base">عرض {offerIndex + 1}</CardTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => removeOffer(offer.id)}
                    >
                      حذف العرض
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>
                          عنوان العرض <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={offer.offer_title}
                          onChange={(e) => updateOffer(offer.id, 'offer_title', e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>
                          نسبة الخصم (%) <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={offer.discount_percentage}
                          onChange={(e) => updateOffer(offer.id, 'discount_percentage', e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>تاريخ البداية</Label>
                        <Input
                          type="date"
                          value={offer.valid_from}
                          onChange={(e) => updateOffer(offer.id, 'valid_from', e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>تاريخ النهاية</Label>
                        <Input
                          type="date"
                          value={offer.valid_until}
                          onChange={(e) => updateOffer(offer.id, 'valid_until', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>وصف العرض</Label>
                      <Textarea
                        value={offer.offer_description}
                        onChange={(e) => updateOffer(offer.id, 'offer_description', e.target.value)}
                        rows={2}
                      />
                    </div>

                    <Separator />

                    {/* Products for this offer */}
                    <div>
                      <h5 className="mb-3 font-medium text-foreground">منتجات العرض</h5>
                      {offer.products.map((product, productIndex) => (
                        <Card key={productIndex} className="mb-3 bg-muted/50">
                          <CardContent className="pt-4">
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-sm font-medium text-foreground">منتج {productIndex + 1}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => removeProduct(offer.id, productIndex)}
                              >
                                حذف
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                              <Input
                                type="text"
                                placeholder="اسم المنتج"
                                value={product.name}
                                onChange={(e) => updateProduct(offer.id, productIndex, 'name', e.target.value)}
                              />
                              <Input
                                type="number"
                                placeholder="السعر"
                                value={product.price}
                                onChange={(e) => updateProduct(offer.id, productIndex, 'price', e.target.value)}
                              />
                            </div>
                            <div className="mt-2">
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleProductImageChange(offer.id, productIndex, e.target.files[0])}
                              />
                              {product.imageFile && (
                                <div className="mt-2">
                                  <img
                                    src={URL.createObjectURL(product.imageFile)}
                                    alt="Product preview"
                                    className="h-16 w-16 rounded-md object-cover"
                                  />
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => addProduct(offer.id)}
                      >
                        + إضافة منتج
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button type="button" variant="outline" onClick={addOffer}>
                + إضافة عرض جديد
              </Button>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button type="submit" size="lg" disabled={loading}>
              {loading ? 'جاري الإرسال...' : '🚀 إرسال طلب التسجيل'}
            </Button>
          </div>
        </form>

        {/* Response Messages */}
        {response && (
          <Card className="mt-6 border-emerald-500/50 bg-emerald-500/10">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-emerald-700 dark:text-emerald-400">✅ تم إرسال طلب التسجيل بنجاح!</h3>
              <p className="mt-2 text-emerald-700 dark:text-emerald-400">{response.message}</p>
              {response.data && (
                <div className="mt-2 text-sm text-muted-foreground">
                  <p>معرف التاجر: {response.data.merchant_id}</p>
                  <p>اسم المتجر: {response.data.business_name}</p>
                  <p>الحالة: {response.data.status}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="mt-6 border-destructive/50 bg-destructive/10">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-destructive">❌ حدث خطأ</h3>
              <p className="mt-2 whitespace-pre-line text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MerchantRegistration;
